import { type Opportunity } from "@entities";
import LocationOffOutlinedIcon from "@mui/icons-material/LocationOffOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { Chip, Stack, Tooltip } from "@mui/material";
import {
  ADMIN_INSTANCE_STATUS_CLOSED,
  ADMIN_INSTANCE_STATUS_FULL,
  ADMIN_INSTANCE_STATUS_OPEN,
  ADMIN_INSTANCE_STATUS_UNTREATED,
  ADMIN_INSTANCE_WARNING_LAST_CALL_PAST,
  ADMIN_INSTANCE_WARNING_LAST_CALL_SOON,
  ADMIN_INSTANCE_WARNING_NO_COORDINATES,
} from "@shared/helpers/opportunity-group-admin-copy";
import {
  computeGroupInstanceAdminStatus,
  isGroupInstanceLastCallSoonOrPassed,
  type GroupInstanceAdminStatus,
} from "@shared/helpers/opportunity-group";
import { type FC, useMemo } from "react";

const STATUS_CHIP: Record<
  GroupInstanceAdminStatus,
  { label: string; color: "default" | "primary" | "warning" | "error" }
> = {
  open: { label: ADMIN_INSTANCE_STATUS_OPEN, color: "primary" },
  full: { label: ADMIN_INSTANCE_STATUS_FULL, color: "warning" },
  untreated: { label: ADMIN_INSTANCE_STATUS_UNTREATED, color: "error" },
  closed: { label: ADMIN_INSTANCE_STATUS_CLOSED, color: "default" },
};

type Props = {
  child: Opportunity;
};

export const GroupInstanceStatusBadge: FC<Props> = ({ child }) => {
  const { primary, warnings } = useMemo(
    () =>
      computeGroupInstanceAdminStatus({
        isClosed: child.isClosed,
        isFull: child.isFull,
        participantsCount: child.participantsCount,
        amount: child.amount,
        lastCallToVolunteers: child.lastCallToVolunteers,
        startDate: child.startDate ?? null,
        endDate: child.endDate ?? null,
        coordinates: child.coordinates,
      }),
    [
      child.isClosed,
      child.isFull,
      child.participantsCount,
      child.amount,
      child.lastCallToVolunteers,
      child.startDate,
      child.endDate,
      child.coordinates,
    ],
  );

  const chip = STATUS_CHIP[primary];
  const lastCallPast =
    child.lastCallToVolunteers != null &&
    new Date(child.lastCallToVolunteers).getTime() < Date.now();
  const lastCallWarningLabel = lastCallPast
    ? ADMIN_INSTANCE_WARNING_LAST_CALL_PAST
    : ADMIN_INSTANCE_WARNING_LAST_CALL_SOON;

  return (
    <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap">
      <Chip
        label={chip.label}
        size="small"
        color={chip.color}
        variant={primary === "closed" ? "outlined" : "filled"}
        sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600 }}
      />
      {warnings.includes("noCoordinates") && (
        <Tooltip title={ADMIN_INSTANCE_WARNING_NO_COORDINATES}>
          <LocationOffOutlinedIcon
            sx={{ fontSize: 16, color: "text.secondary" }}
            aria-label={ADMIN_INSTANCE_WARNING_NO_COORDINATES}
          />
        </Tooltip>
      )}
      {warnings.includes("lastCallSoon") &&
        isGroupInstanceLastCallSoonOrPassed(child.lastCallToVolunteers) && (
          <Tooltip title={lastCallWarningLabel}>
            <ScheduleIcon
              sx={{ fontSize: 16, color: "text.secondary" }}
              aria-label={lastCallWarningLabel}
            />
          </Tooltip>
        )}
    </Stack>
  );
};
